import { forwardRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface BudgetPrintViewProps {
  budget: any;
}

const BudgetPrintView = forwardRef<HTMLDivElement, BudgetPrintViewProps>(
  ({ budget }, ref) => {
    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value || 0);
    };

    const statusLabels: Record<string, string> = {
      draft: "Rascunho",
      sent: "Enviado",
      viewed: "Visualizado",
      approved: "Aprovado",
      rejected: "Rejeitado",
      expired: "Expirado",
      converted: "Convertido",
    };

    const company = budget.company || {};

    return (
      <>
        <div
          ref={ref}
          className="print-area bg-white text-gray-900 p-8 max-w-4xl mx-auto"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-gray-300">
            <div>
              {budget.company?.logo_url ? (
                <img
                  src={budget.company.logo_url}
                  alt={budget.company?.name}
                  className="h-16 mb-2"
                />
              ) : (
                <h1
                  className="text-2xl font-bold"
                  style={{ color: budget.company?.primary_color || "#ff6b35" }}
                >
                  {budget.company?.name || "Empresa"}
                </h1>
              )}
              <div className="text-sm text-gray-600 mt-2">
                {budget.company?.email && <p>{budget.company.email}</p>}
                {budget.company?.phone && <p>{budget.company.phone}</p>}
                {budget.company?.document && (
                  <p>CNPJ: {budget.company.document}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-bold text-gray-800">ORÇAMENTO</h2>
              <p className="text-xl font-mono mt-2">{budget.number}</p>
              <p className="text-sm text-gray-600 mt-2">
                Data: {format(new Date(budget.created_at), "dd/MM/yyyy")}
              </p>
              <p className="text-sm text-gray-600">
                Validade:{" "}
                {budget.validity_date
                  ? format(new Date(budget.validity_date), "dd/MM/yyyy")
                  : "Não definida"}
              </p>
              <span
                className="inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold"
                style={{
                  backgroundColor:
                    budget.status === "approved"
                      ? "#dcfce7"
                      : budget.status === "rejected"
                        ? "#fee2e2"
                        : "#f3f4f6",
                  color:
                    budget.status === "approved"
                      ? "#166534"
                      : budget.status === "rejected"
                        ? "#991b1b"
                        : "#374151",
                }}
              >
                {statusLabels[budget.status] || budget.status}
              </span>
            </div>
          </div>

          {/* Client Info */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Cliente
            </h3>
            {budget.client ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold text-gray-900">
                    {budget.client.name}
                  </p>
                  {budget.client.trade_name && (
                    <p className="text-gray-600">{budget.client.trade_name}</p>
                  )}
                  {budget.client.document && (
                    <p className="text-sm text-gray-600">
                      {budget.client.type === "pj" ? "CNPJ" : "CPF"}:{" "}
                      {budget.client.document}
                    </p>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {budget.client.email && <p>Email: {budget.client.email}</p>}
                  {budget.client.phone && (
                    <p>Telefone: {budget.client.phone}</p>
                  )}
                  {budget.client.address_city && (
                    <p>
                      {[
                        budget.client.address_street,
                        budget.client.address_number,
                        budget.client.address_neighborhood,
                        budget.client.address_city,
                        budget.client.address_state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Cliente não informado</p>
            )}
          </div>

          {/* Title & Description */}
          {(budget.title || budget.description) && (
            <div className="mb-6">
              {budget.title && (
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {budget.title}
                </h3>
              )}
              {budget.description && (
                <p className="text-gray-600 whitespace-pre-wrap">
                  {budget.description}
                </p>
              )}
            </div>
          )}

          {/* Items Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Itens</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">
                    Descrição
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold w-20">
                    Qtd
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold w-32">
                    Valor Unit.
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold w-24">
                    Desconto
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold w-32">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {budget.items?.map((item: any, index: number) => (
                  <tr key={item.id || index}>
                    <td className="border border-gray-300 px-4 py-3">
                      <p className="font-medium">
                        {item.name || item.description}
                      </p>
                      {item.description && item.name !== item.description && (
                        <p className="text-sm text-gray-500">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-mono">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-mono">
                      {formatCurrency(item.unit_price)}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-mono text-red-600">
                      {item.discount_value > 0
                        ? item.discount_type === "percent"
                          ? `-${item.discount_value}%`
                          : `-${formatCurrency(item.discount_value)}`
                        : "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-mono font-semibold">
                      {formatCurrency(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-72">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-mono">
                  {formatCurrency(budget.subtotal)}
                </span>
              </div>
              {budget.discount_value > 0 && (
                <div className="flex justify-between py-2 border-b border-gray-200 text-red-600">
                  <span>
                    Desconto{" "}
                    {budget.discount_type === "percent"
                      ? `(${budget.discount_value}%)`
                      : ""}
                  </span>
                  <span className="font-mono">
                    -
                    {formatCurrency(
                      budget.discount_type === "percent"
                        ? (budget.subtotal || 0) * (budget.discount_value / 100)
                        : budget.discount_value,
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between py-3 border-b-2 border-gray-800">
                <span className="text-lg font-bold">Total</span>
                <span
                  className="text-xl font-bold font-mono"
                  style={{ color: budget.company?.primary_color || "#ff6b35" }}
                >
                  {formatCurrency(budget.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          {(budget.payment_terms ||
            budget.terms ||
            budget.payment_conditions) && (
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Condições
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {budget.payment_conditions && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">
                      Pagamento
                    </h4>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {budget.payment_conditions}
                    </p>
                  </div>
                )}
                {budget.terms && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Termos</h4>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {budget.terms}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {budget.notes && (
            <div className="mb-8 p-4 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Observações
              </h3>
              <p className="text-gray-600 whitespace-pre-wrap">
                {budget.notes}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
            <p>
              Este orçamento foi gerado em{" "}
              {format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                locale: ptBR,
              })}
            </p>
            {budget.public_token && (
              <p className="mt-1">
                Código de verificação: {budget.public_token.slice(0, 8)}...
              </p>
            )}
          </div>
        </div>

        {/* Detailed Descriptions Page - Only if any item has detailed_description */}
        {budget.items?.some((item: any) => item.detailed_description) && (
          <div className="bg-white p-8 print:p-6 max-w-4xl mx-auto print:max-w-none print:break-before-page">
            {/* Header for detailed descriptions page */}
            <div className="border-b border-gray-300 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.name}
                      className="h-12 w-auto object-contain"
                    />
                  ) : (
                    <h2 className="text-xl font-bold text-gray-800">
                      {company.name}
                    </h2>
                  )}
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p className="font-semibold text-gray-800">
                    Orçamento #{budget.number}
                  </p>
                  <p>Anexo: Descrição Detalhada dos Itens</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Escopo dos Produtos e Serviços
            </h2>

            {budget.items
              .filter((item: any) => item.detailed_description)
              .map((item: any, index: number) => (
                <div
                  key={item.id || index}
                  className="mb-8 pb-6 border-b border-gray-200 last:border-b-0"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {index + 1}. {item.name}
                    </h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Qtd: {item.quantity} {item.unit}
                    </span>
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-600 italic mb-3">
                      {item.description}
                    </p>
                  )}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2 text-sm uppercase tracking-wide">
                      Escopo / Funções / Capacidades
                    </h4>
                    <div className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {item.detailed_description}
                    </div>
                  </div>
                </div>
              ))}

            {/* Footer for detailed descriptions page */}
            <div className="mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
              <p>
                Este documento é parte integrante do Orçamento #{budget.number}
              </p>
            </div>
          </div>
        )}
      </>
    );
  },
);

BudgetPrintView.displayName = "BudgetPrintView";

export default BudgetPrintView;
